import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import { FlexRow, Link, TagButton } from "atoms";
import iconCloseSrc from "atoms/icons/close.svg";
import { parseFilters } from "../utils/parseFilters";
import { removeURLParam } from "../utils/removeURLParam";

function ActiveFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasFilters =
    [...searchParams.entries()].filter(
      ([name]) => name !== "name" && name !== "sort"
    ).length > 0;

  const {
    type,
    founderAttributes = [],
    missionOrCause = [],
    blockchain = [],
    mintStatus = [],
    doxxed = [],
    collectionSize,
    mintPrice,
    floorPrice,
    mintDate,
    badges,
    roadmapCategories,
  } = parseFilters(searchParams);

  // Create event handler to remove one value in array filter keys
  const createHandler = (key: string, value?: string) => () => {
    setSearchParams(removeURLParam(searchParams, key, value));
  };

  // Create event handler to remove both keys for range filter
  const createRangeHandler = (key: string) => () => {
    setSearchParams(
      removeURLParam(removeURLParam(searchParams, `${key}Gte`), `${key}Lte`)
    );
  };

  const sort = searchParams.get("sort");

  if (!hasFilters) return null;

  return (
    <SFlexRow>
      {type && (
        <ActiveFilter
          onClick={createHandler("type", type)}
          aria-label="Remove type from active filters"
        >
          {type[0].toUpperCase()}
          {type.slice(1)}
        </ActiveFilter>
      )}

      {founderAttributes.map((tag) => (
        <FounderTagActiveFilter
          key={tag}
          onClick={createHandler("founderAttributes", tag)}
          aria-label={`Remove ${tag} from active filters`}
        >
          {tag}
        </FounderTagActiveFilter>
      ))}

      {missionOrCause.map((tag) => (
        <MissionTagActiveFilter
          key={tag}
          onClick={createHandler("missionOrCause", tag)}
          aria-label={`Remove ${tag} from active filters`}
        >
          {tag}
        </MissionTagActiveFilter>
      ))}

      {blockchain?.map((filter) => (
        <ActiveFilter
          key={filter}
          onClick={createHandler("blockchain", filter)}
          aria-label="Remove blockchain from active filters"
        >
          {filter}
        </ActiveFilter>
      ))}

      {roadmapCategories?.map((filter) => (
        <ActiveFilter
          key={filter}
          onClick={createHandler("roadmapCategories", filter)}
          aria-label="Remove roadmap category from active filters"
        >
          {filter}
        </ActiveFilter>
      ))}

      {badges?.map((filter) => (
        <ActiveFilter
          key={filter}
          onClick={createHandler("badges", filter)}
          aria-label="Remove badge from active filters"
        >
          {filter}
        </ActiveFilter>
      ))}

      {mintStatus?.map((filter) => (
        <ActiveFilter
          key={filter}
          onClick={createHandler("mintStatus", filter)}
          aria-label="Remove mint status from active filters"
        >
          {filter}
        </ActiveFilter>
      ))}

      {doxxed?.map((filter) => (
        <ActiveFilter
          key={filter}
          onClick={createHandler("doxxed", filter)}
          aria-label="Remove doxxed status from active filters"
        >
          {filter}
        </ActiveFilter>
      ))}

      {collectionSize && (
        <ActiveFilter
          onClick={createRangeHandler("collectionSize")}
          aria-label="Remove collection size from active filters"
        >
          Collection Size
        </ActiveFilter>
      )}

      {mintPrice && (
        <ActiveFilter
          onClick={createRangeHandler("mintPrice")}
          aria-label="Remove mint price from active filters"
        >
          Mint Price
        </ActiveFilter>
      )}

      {floorPrice && (
        <ActiveFilter
          onClick={createRangeHandler("floorPrice")}
          aria-label="Remove floor price from active filters"
        >
          Floor Price
        </ActiveFilter>
      )}

      {mintDate && (
        <ActiveFilter
          onClick={createRangeHandler("mintDate")}
          aria-label="Remove mint date from active filters`"
        >
          Mint Date
        </ActiveFilter>
      )}

      <ClearLink to={sort ? `/search?sort=${sort}` : "/search"}>
        Clear all filters
      </ClearLink>
    </SFlexRow>
  );
}
const SFlexRow = styled(FlexRow)({
  gap: 10,
  paddingTop: "2rem",
  "&>*": {
    flex: "0 0 auto",
  },
});
const ActiveFilter = styled(TagButton)(({ theme }) => ({
  background: theme.colors.pink,
  fontSize: theme.fontSizes.xxs,
  position: "relative",
  paddingLeft: "1.4rem",
  flex: "0 0 auto",
  minHeight: 0,
  "&::before": {
    background: `url("${iconCloseSrc}") no-repeat center center`,
    content: "''",
    height: 8,
    left: "0.6rem",
    opacity: 0.7,
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 8,
  },
  "&:hover::before": {
    transform: "translateY(-50%)",
  },
}));
const ClearLink = styled(Link)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  opacity: 0.66,
  position: "relative",
  fontSize: theme.fontSizes.xxs,
  textDecoration: "none",
  borderBottom: "1px solid transparent",
  lineHeight: 1.3,
  "&::before": {
    background: `url("${iconCloseSrc}") no-repeat center center`,
    content: "''",
    display: "block",
    height: 6,
    width: 6,
    marginRight: 5,
  },
  "&:hover, &:focus-visible": {
    opacity: 1,
    color: theme.colors.fg,
    borderBottomColor: "currentColor",
  },
}));

const FounderTagActiveFilter = styled(ActiveFilter)(({ theme }) => ({
  backgroundImage: theme.gradients.tagFounder,
  textTransform: "unset",
}));

const MissionTagActiveFilter = styled(ActiveFilter)(({ theme }) => ({
  backgroundImage: theme.gradients.tagMission,
  textTransform: "unset",
}));

export { ActiveFilters };
